import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Site-wide light/dark theme controller.
 *
 * Storage model (localStorage key: `sas-theme`):
 *   - "light"  → user explicitly chose light
 *   - "dark"   → user explicitly chose dark
 *   - absent   → follow OS preference (`prefers-color-scheme: dark`)
 *
 * Side-effects on the <html> element so both Tailwind's `dark:` variant
 * (configured as `darkMode: ["class"]`) and the CSS custom-property
 * overrides defined in `index.css` (under `:root.dark` / `[data-theme=...]`)
 * apply consistently:
 *   - `class="dark"` is added/removed
 *   - `data-theme="dark" | "light"` is set
 *   - `color-scheme` style is set so native form controls / scrollbars
 *     match the active theme
 *
 * `theme` is the user's stored preference (or "system" when absent).
 * `resolvedTheme` is what is actually rendered ("light" | "dark") after
 * resolving the system preference.
 */

const STORAGE_KEY = "sas-theme";
const ThemeContext = createContext(null);

function readStoredPreference() {
  if (typeof window === "undefined") return "system";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
    return "system";
  } catch {
    return "system";
  }
}

function getSystemPreference() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolve(theme) {
  return theme === "system" ? getSystemPreference() : theme;
}

function applyToDocument(resolved) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.setAttribute("data-theme", resolved);
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }) {
  // Lazy initialisation so the very first paint already reflects the
  // resolved theme (avoids a light → dark flicker on dark-mode users).
  const [theme, setTheme] = useState(() => readStoredPreference());
  const [resolved, setResolved] = useState(() => resolve(readStoredPreference()));

  // Push the resolved theme into the document on every change.
  useEffect(() => {
    applyToDocument(resolved);
  }, [resolved]);

  // Re-resolve when the user's stored preference changes.
  useEffect(() => {
    setResolved(resolve(theme));
  }, [theme]);

  // If the user is on "system", live-update when the OS toggles dark mode.
  useEffect(() => {
    if (theme !== "system") return undefined;
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setResolved(mql.matches ? "dark" : "light");
    // addEventListener is the modern API; older Safari needs addListener.
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [theme]);

  const setThemePreference = useCallback((next) => {
    if (next !== "light" && next !== "dark" && next !== "system") return;
    setTheme(next);
    try {
      if (next === "system") {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, next);
      }
    } catch {
      /* private mode / storage unavailable — non-fatal */
    }
  }, []);

  /**
   * Binary toggle: flips between light and dark and persists the
   * explicit choice. After a manual toggle the user is no longer on
   * "system" mode (their pick is now sticky).
   */
  const toggleTheme = useCallback(() => {
    const next = resolved === "dark" ? "light" : "dark";
    setThemePreference(next);
  }, [resolved, setThemePreference]);

  const value = useMemo(
    () => ({
      theme, // "light" | "dark" | "system"
      resolvedTheme: resolved, // "light" | "dark" — what is actually rendered
      isDark: resolved === "dark",
      setTheme: setThemePreference,
      toggleTheme,
    }),
    [theme, resolved, setThemePreference, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Defensive default so any component that mounts before the provider
    // (e.g. during early hydration) gets a usable shape.
    return {
      theme: "light",
      resolvedTheme: "light",
      isDark: false,
      setTheme: () => {},
      toggleTheme: () => {},
    };
  }
  return ctx;
}
